import { useEffect, useMemo } from 'react';
import type { ReactElement } from 'react';
import type {
  PicklistPluginElement,
  PicklistPluginPropertyOptions,
  ReportPicklistBeforeSubmissionOptions,
} from '../types';

import { PicklistBuilder } from './PicklistBuilder';
import type { GetRenderElementComponentType } from '../../../utils/getRenderElement';
import { unreachableCaseError } from 'types';
import { useSelected, useSlate } from '../../../core';

import { useReporterState } from '../../../hooks';

import { PicklistSelector } from './PicklistSelector';
import { usePicklistEditor } from '../../../../Template/usePicklist';
import { isNodeTypeIn } from '../../../utils';
import { Editor, Path, NodeEntry } from 'slate';
import { ReactEditor } from 'slate-react';
import { isSelectionContainedInNode } from '../../../utils/isSelectionContainedInNode';
import { getActivePicklist } from '../../../utils/selection';

const EMPTY_ARRAY: Array<ReportPicklistBeforeSubmissionOptions> = [];

export const PicklistEditable: GetRenderElementComponentType<
  PicklistPluginPropertyOptions,
  PicklistPluginElement
> = ({
  pluginOptions: { pluginID, showPicklistOptionsInEditor },
  element,
  ...rest
}): ReactElement | null => {
  const { variant } = useReporterState();
  const { picklists, setActivePicklist, activePicklist } = usePicklistEditor();
  const selected = useSelected();

  const editor = useSlate();

  const isSelected = useMemo(() => {
    if (Editor.isEditor(editor) && editor.selection != null) {
      try {
        // innermost picklist at the selection (nested scenarios)
        const selectionNode = getActivePicklist(
          editor,
          editor.selection
        ) as NodeEntry<PicklistPluginElement> | null;

        if (selectionNode != null) {
          // Compare paths to see if this element is the selected picklist
          const currentElementPath = ReactEditor.findPath(editor, element);
          const selectionNodePath = selectionNode[1];
          const pathsMatch = Path.equals(currentElementPath, selectionNodePath);

          return pathsMatch;
        }

        // fallback element check if selection is contained in this element
        const currentElementPath = ReactEditor.findPath(editor, element);
        const isContained = isSelectionContainedInNode(
          editor,
          currentElementPath,
          editor.selection
        );

        return isContained;
      } catch (error) {
        return selected;
      }
    }
    return selected;
  }, [editor, element, selected]);

  useEffect(() => {
    if (isSelected) {
      const activePicklist = picklists.find((p) => p.id === element.picklistID);

      // always set activePicklist, even if undefined - this ensures the effect re-runs
      // when picklists are loaded (e.g., when loading a saved report)
      setActivePicklist(activePicklist ?? null);
    } else if (!isNodeTypeIn(editor, pluginID)) {
      setActivePicklist(null);
    }

    // when a picklist is 'unmounted', either because it was deleted or a new template was applied, reset active picklist.
    return () => {
      setActivePicklist(null);
    };
  }, [
    isSelected,
    editor,
    pluginID,
    element.picklistID,
    element.name,
    setActivePicklist,
    picklists,
  ]);

  switch (variant) {
    case 'report':
      return (
        <PicklistSelector
          {...rest}
          isSelected={isSelected}
          pluginID={pluginID}
          showPicklistOptionsInEditor={showPicklistOptionsInEditor}
          options={activePicklist?.options ?? EMPTY_ARRAY}
          element={element}
        />
      );
    case 'fragment':
    case 'template':
      return (
        <PicklistBuilder
          {...rest}
          variant={variant}
          options={activePicklist?.options ?? []}
          element={element}
        />
      );
    case 'view':
      return null;
    default:
      unreachableCaseError(variant);
      return null;
  }
};
